import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Button } from '@heroui/button';
import { Card, CardBody } from '@heroui/card';
import { useDisclosure } from '@heroui/modal';
import { Spinner } from '@heroui/spinner';
import { Tab, Tabs } from '@heroui/tabs';
import { addToast } from '@heroui/toast';
import { useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { useBillRequestControllerStatus } from '@/api/generated/bill-requests/bill-requests';
import { useContractsControllerFindByRenewalSiteList } from '@/api/generated/contracts/contracts';
import type { ExportJobDto, QueueExportDtoType, SiteListSiteDto } from '@/api/generated/schemas';
import { SiteDtoBillType } from '@/api/generated/schemas';
import {
  getSiteListsControllerFindOneQueryKey,
  getSiteListsControllerFindSitesForSiteListQueryKey,
  useSiteListsControllerSyncRyo,
  useSiteListsControllerUnlinkDeal,
} from '@/api/generated/site-lists/site-lists';
import { BillUploadModal } from '@/components/bills';
import { Icon } from '@/components/common';
import { LinkContractToSiteListModal } from '@/components/link-site-list-contract';
import {
  BillRequestBanner,
  CloseReviewDrawer,
  DealCard,
  DeleteSiteListModal,
  ImportSitesCsvModal,
  LinkDealModal,
  LinkExistingSiteModal,
  PrepareLoaModal,
  RequestBillsModal,
  RyoSyncCard,
  SiteImportBanner,
  SiteImportReviewModal,
  SiteListBillImportsTab,
  SiteListExportsTab,
  SiteListHeader,
  SiteListRenewalsTab,
  SiteListSitesTab,
  SiteListValueCard,
  TENDER_CHECKLIST_TASK_COUNT,
  TenderChecklistTab,
  countTenderChecklistTicks,
  isSiteListExport,
  useSiteListExportToasts,
} from '@/components/site-lists';
import { AddSiteModal, MoveSiteToListModal } from '@/components/sites';
import { useAuth } from '@/firebase/useAuth';
import { useSiteListRealtimeUpdates } from '@/hooks/useFirestoreSubscriptions';
import {
  useBillImportsBySiteList,
  useDeleteBillImport,
  useReExtractBillsForSiteList,
  useRetryBillImport,
} from '@/queries/bills.queries';
import {
  useCreateExportJob,
  useDeleteExportJob,
  useDownloadExport,
  useExportJobs,
} from '@/queries/export-jobs.queries';
import { useDismissSiteImport, useSiteImports } from '@/queries/site-imports.queries';
import { useRemoveSiteFromList, useSiteList } from '@/queries/site-lists.queries';
import { useSitesBySiteList } from '@/queries/sites.queries';
import { canSyncRyo } from '@/utils/ryoEligibility';

export const Route = createFileRoute('/site-lists/$siteListId')({
  component: SiteListDetail,
});

function SiteListDetail() {
  const { siteListId } = Route.useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Contracts being renewed in this list (drives the conditional Renewals tab)
  const { data: renewalContracts = [] } = useContractsControllerFindByRenewalSiteList(siteListId);

  // Fetch export jobs for this site list only
  const { data: exportJobs = [], isSuccess: exportJobsLoaded } = useExportJobs(siteListId);
  const { data: siteImports = [] } = useSiteImports(siteListId);
  const dismissSiteImportMutation = useDismissSiteImport();
  const createExportMutation = useCreateExportJob();
  const downloadExportMutation = useDownloadExport();
  const deleteExportMutation = useDeleteExportJob();

  const { isOpen: isAddSiteOpen, onOpen: onAddSiteOpen, onClose: onAddSiteClose } = useDisclosure();
  const {
    isOpen: isLinkExistingSiteOpen,
    onOpen: onLinkExistingSiteOpen,
    onClose: onLinkExistingSiteClose,
  } = useDisclosure();
  const { isOpen: isBillUploadOpen, onOpen: onBillUploadOpen, onClose: onBillUploadClose } = useDisclosure();
  const {
    isOpen: isImportSitesCsvOpen,
    onOpen: onImportSitesCsvOpen,
    onClose: onImportSitesCsvClose,
  } = useDisclosure();
  const [reviewSiteImportId, setReviewSiteImportId] = useState<string | null>(null);
  const reviewSiteImport = siteImports.find((job) => job.id === reviewSiteImportId) ?? null;
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { isOpen: isLinkDealOpen, onOpen: onLinkDealOpen, onClose: onLinkDealClose } = useDisclosure();
  const { isOpen: isLinkContractOpen, onOpen: onLinkContractOpen, onClose: onLinkContractClose } = useDisclosure();
  const { isOpen: isPrepareLoaOpen, onOpen: onPrepareLoaOpen, onClose: onPrepareLoaClose } = useDisclosure();
  const { isOpen: isRequestBillsOpen, onOpen: onRequestBillsOpen, onClose: onRequestBillsClose } = useDisclosure();
  const { isOpen: isMoveModalOpen, onOpen: onMoveModalOpen, onClose: onMoveModalClose } = useDisclosure();
  const [sitesToMove, setSitesToMove] = useState<string[]>([]);
  const [reviewSite, setReviewSite] = useState<SiteListSiteDto | null>(null);
  const [tab, setTab] = useState('sites');

  // Exports finished since the tab was last open. Seeded from the first load so
  // old exports don't badge, then caught up each time the user leaves the tab.
  const [seenExportIds, setSeenExportIds] = useState<ReadonlySet<string> | null>(null);
  const completedExportIds = useMemo(
    () => exportJobs.filter((j) => isSiteListExport(j) && j.status === 'completed').map((j) => j.id),
    [exportJobs],
  );
  // Seeded during render, not an effect, per React's "you might not need an effect" guidance.
  if (exportJobsLoaded && seenExportIds === null) setSeenExportIds(new Set(completedExportIds));
  const selectTab = (next: string) => {
    if (tab === 'exports' && next !== 'exports') setSeenExportIds(new Set(completedExportIds));
    setTab(next);
  };
  const unseenExportCount =
    tab === 'exports' || !seenExportIds ? 0 : completedExportIds.filter((id) => !seenExportIds.has(id)).length;
  const exportRunning = exportJobs.some(
    (j) => isSiteListExport(j) && (j.status === 'pending' || j.status === 'processing'),
  );

  // Set up real-time subscriptions for this site list
  useSiteListRealtimeUpdates(siteListId);

  // Use custom query hooks
  const { data: siteList, isLoading: siteListLoading, error: siteListError } = useSiteList(siteListId);

  // Fetch up to the first 200 sites just to size the RYO sync card (count is
  // best-effort for lists >200; the actual sync runs server-side on every member).
  const {
    data: sitesPage,
    isLoading: sitesLoading,
    error: sitesError,
  } = useSitesBySiteList(siteListId, { pageSize: '200' });

  const { data: billImports = [], isLoading: billImportsLoading } = useBillImportsBySiteList(siteListId);
  const { data: billRequestStatus } = useBillRequestControllerStatus(siteListId);

  const deleteBillImportMutation = useDeleteBillImport();
  const retryBillImportMutation = useRetryBillImport();
  const reExtractBillsMutation = useReExtractBillsForSiteList();
  const removeSiteMutation = useRemoveSiteFromList();

  const queryClient = useQueryClient();
  const syncRyoMutation = useSiteListsControllerSyncRyo<Error>({
    mutation: {
      onSuccess: (result) => {
        const parts: string[] = [];
        if (result.tenderEnqueued) parts.push('tender creation queued');
        if (result.calcsEnqueued) parts.push(`${result.calcsEnqueued} calc(s) queued`);
        if (result.skippedAlreadySynced) parts.push(`${result.skippedAlreadySynced} already synced`);
        if (result.skippedIneligible) parts.push(`${result.skippedIneligible} ineligible`);
        addToast({
          title: 'Sync queued',
          description: parts.length ? parts.join(', ') : 'Nothing to sync',
          color: 'success',
        });
        void queryClient.invalidateQueries({ queryKey: getSiteListsControllerFindOneQueryKey(siteListId) });
        void queryClient.invalidateQueries({
          queryKey: getSiteListsControllerFindSitesForSiteListQueryKey(siteListId),
        });
      },
      onError: (error) => {
        addToast({
          title: 'Sync failed',
          description: error instanceof Error ? error.message : 'Failed to queue RYO sync',
          color: 'danger',
        });
      },
    },
  });

  const unlinkDealMutation = useSiteListsControllerUnlinkDeal();
  const handleUnlinkDeal = async () => {
    if (confirm('Are you sure you want to unlink this Deal from the site list?')) {
      try {
        await unlinkDealMutation.mutateAsync({ id: siteListId });
        addToast({ title: 'Success', description: 'Deal unlinked successfully', color: 'success' });
        void queryClient.invalidateQueries({ queryKey: getSiteListsControllerFindOneQueryKey(siteListId) });
      } catch (error) {
        addToast({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to unlink Deal',
          color: 'danger',
        });
      }
    }
  };

  // The tender checklist is an SME workflow, so it only shows once the list has an SME site.
  // The rollup covers lists longer than the page we fetched; the page covers a rollup not yet run.
  const hasSmeSites =
    (siteList?.usageAnalysis?.smeSiteCount ?? 0) > 0 ||
    (sitesPage?.items ?? []).some((s) => s.billType === SiteDtoBillType.SME);

  const eligibleUnsyncedCount = useMemo(() => (sitesPage?.items ?? []).filter(canSyncRyo).length, [sitesPage]);
  // When the page we fetched is full AND the list has more, we may be
  // undercounting the eligible-unsynced total. Surface as "N+" in the UI
  // rather than asserting an exact figure we don't actually know.
  const eligibleUnsyncedCountIsApprox =
    !!sitesPage && sitesPage.hasMore && (sitesPage.items?.length ?? 0) === sitesPage.pageSize;

  // LOA "site 1" — apply the same connectionPointId sort the backend uses, so the
  // prefilled signer matches the site that fills the LOA's main block regardless
  // of the order the sites endpoint returns. (Exact for lists within one page.)
  const loaPrimaryContact = useMemo(
    () =>
      [...(sitesPage?.items ?? [])].sort((a, b) => a.connectionPointId.localeCompare(b.connectionPointId))[0]
        ?.primaryContact,
    [sitesPage],
  );

  const isLoading = siteListLoading || sitesLoading;
  const error = siteListError || sitesError;

  const handleRetryBillImport = async (billImportId: string) => {
    try {
      await retryBillImportMutation.mutateAsync(billImportId);
      addToast({ title: 'Re-extraction queued', color: 'success' });
    } catch (error) {
      addToast({
        title: 'Re-extract failed',
        description: error instanceof Error ? error.message : 'Failed to queue re-extraction',
        color: 'danger',
      });
    }
  };

  const handleReExtractAll = async () => {
    try {
      const result = await reExtractBillsMutation.mutateAsync(siteListId);
      const parts = [
        `${result.requeuedFailed} failed retried`,
        `${result.requeuedCompleted} completed re-extracted`,
        result.skipped > 0 ? `${result.skipped} skipped` : null,
      ].filter(Boolean);
      addToast({
        title: `Queued ${result.queued} re-extractions`,
        description: parts.join(' • '),
        color: 'success',
      });
    } catch (error) {
      addToast({
        title: 'Re-extract all failed',
        description: error instanceof Error ? error.message : 'Failed to queue bulk re-extraction',
        color: 'danger',
      });
    }
  };

  const handleDeleteBillImport = async (billImportId: string) => {
    if (confirm('Are you sure you want to delete this bill import? This action cannot be undone.')) {
      try {
        await deleteBillImportMutation.mutateAsync(billImportId);
      } catch (error) {
        console.error('Failed to delete bill import:', error);
        // Could add toast notification here
      }
    }
  };

  const handleRemoveSite = async ({ id, connectionPointId }: SiteListSiteDto) => {
    if (confirm(`Are you sure you want to remove ${connectionPointId} from this site list?`)) {
      try {
        await removeSiteMutation.mutateAsync({ siteListId, siteId: id });
        addToast({
          title: 'Success',
          description: `${connectionPointId} has been removed from the site list`,
          color: 'success',
        });
      } catch (error) {
        addToast({
          title: 'Error',
          description: `Could not remove ${connectionPointId}: ${error instanceof Error ? error.message : 'An unexpected error occurred'}`,
          color: 'danger',
        });
      }
    }
  };

  const queueExport = (type: QueueExportDtoType) => {
    createExportMutation.mutate(
      { siteListId, type },
      {
        onError: (error) => {
          console.error('Failed to create export:', error);
          addToast({
            title: 'Error',
            description: error instanceof Error ? error.message : 'An unexpected error occurred',
            color: 'danger',
          });
        },
      },
    );
  };

  const handleDownloadExport = async (job: ExportJobDto) => {
    try {
      await downloadExportMutation.mutateAsync(job);
    } catch (error) {
      console.error('Failed to download export:', error);
      addToast({
        title: 'Error',
        description: 'Failed to download export. Please try again.',
        color: 'danger',
      });
    }
  };

  useSiteListExportToasts(exportJobs, { onDownload: handleDownloadExport, onViewExports: () => selectTab('exports') });

  const handleDismissExport = async (jobId: string) => {
    try {
      await deleteExportMutation.mutateAsync({ jobId, siteListId });
    } catch (error) {
      console.error('Failed to dismiss export:', error);
      addToast({
        title: 'Error',
        description: 'Failed to dismiss export',
        color: 'danger',
      });
    }
  };

  const handleDeleteList = () => {
    setIsDeleteModalOpen(true);
  };

  const handleDeleteListCompleted = () => {
    navigate({ to: '/site-lists' });
  };

  const handleMoveSites = (siteIds: string[]) => {
    setSitesToMove(siteIds);
    onMoveModalOpen();
  };

  // Handle loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  // Handle error state
  if (error || !siteList) {
    return (
      <div className="py-6">
        <Card className="bg-danger-50 border-danger-200">
          <CardBody className="text-center py-12">
            <Icon as={ExclamationTriangleIcon} size="2xl" color="danger" className="mx-auto mb-3" />
            <p className="text-danger-700">Failed to load site list</p>
            <p className="text-sm text-danger-500 mt-1">The site list could not be found or loaded</p>
            <Link to="/site-lists">
              <Button color="primary" className="mt-4">
                Back to Site Lists
              </Button>
            </Link>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="py-6">
      <div className="flex flex-col gap-5">
        {siteList.wonDealWithoutContract && (
          <div className="flex items-start gap-3 p-4 rounded-medium bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800">
            <Icon as={ExclamationTriangleIcon} size="md" color="warning" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-warning-700">Deal won, no contract linked</p>
              <p className="text-xs text-default-600 mt-1">
                This deal is Closed Won but no contract is linked to it, so its revenue isn't counted. Create the
                contract if it doesn't exist yet, then link it here.
              </p>
            </div>
            <Button size="sm" variant="flat" color="warning" onPress={onLinkContractOpen}>
              Link contract
            </Button>
          </div>
        )}
        {/* Header */}
        <SiteListHeader
          siteList={siteList}
          onBack={() => navigate({ to: '..' })}
          onExportBills={() => queueExport('site_list_bills_csv')}
          onExportSites={() => queueExport('site_list_sites_xlsx')}
          onEditList={() => {
            /* TODO: Implement edit list */
          }}
          onDeleteList={handleDeleteList}
          onAddSiteNew={onAddSiteOpen}
          onAddSiteExisting={onLinkExistingSiteOpen}
          onUploadBills={onBillUploadOpen}
          onImportSitesCsv={onImportSitesCsvOpen}
          onLinkDeal={onLinkDealOpen}
          onLinkContract={onLinkContractOpen}
          onPrepareLoa={onPrepareLoaOpen}
          onRequestBills={onRequestBillsOpen}
          billsRequested={!!billRequestStatus?.request}
        />

        <BillRequestBanner siteListId={siteListId} />

        {/* CSV site import progress and review prompts */}
        {siteImports.length > 0 && (
          <div className="flex flex-col gap-3">
            {siteImports.map((job) => (
              <SiteImportBanner
                key={job.id}
                siteImport={job}
                onReview={setReviewSiteImportId}
                onDismiss={(siteImportId) => dismissSiteImportMutation.mutate({ siteListId, siteImportId })}
                isDismissing={
                  dismissSiteImportMutation.isPending && dismissSiteImportMutation.variables?.siteImportId === job.id
                }
              />
            ))}
          </div>
        )}

        {/* Summary cards */}
        <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(300px,1fr))]">
          <SiteListValueCard siteList={siteList} />
          <DealCard
            dealId={siteList.dealId}
            siteListName={siteList.name}
            onLinkDeal={onLinkDealOpen}
            onUnlinkDeal={handleUnlinkDeal}
            isUnlinking={unlinkDealMutation.isPending}
          />
          <RyoSyncCard
            ryoTenderId={siteList.ryoTenderId}
            ryoSync={siteList.ryoSync}
            hasDeal={!!siteList.dealId}
            eligibleUnsyncedCount={eligibleUnsyncedCount}
            eligibleUnsyncedCountIsApprox={eligibleUnsyncedCountIsApprox}
            isSyncing={syncRyoMutation.isPending}
            onSync={() => syncRyoMutation.mutate({ id: siteListId })}
          />
        </div>

        {/* Tabs Section */}
        <Tabs
          aria-label="Site list tabs"
          variant="solid"
          selectedKey={
            (tab === 'renewals' && renewalContracts.length === 0) || (tab === 'checklist' && !hasSmeSites)
              ? 'sites'
              : tab
          }
          onSelectionChange={(key) => selectTab(String(key))}
          classNames={{ tabList: 'bg-default-100 p-1 gap-0.5', cursor: 'bg-white shadow-sm', tab: 'h-8 px-3' }}
        >
          <Tab key="sites" title="Sites">
            <SiteListSitesTab
              siteListId={siteListId}
              siteListName={siteList.name}
              onRemoveSite={handleRemoveSite}
              onAddSiteNew={onAddSiteOpen}
              onAddSiteExisting={onLinkExistingSiteOpen}
              onUploadBills={onBillUploadOpen}
              onImportSitesCsv={onImportSitesCsvOpen}
              onMoveSites={handleMoveSites}
              onOpenReview={setReviewSite}
            />
          </Tab>

          {renewalContracts.length > 0 && (
            <Tab key="renewals" title={`Renewals (${renewalContracts.length})`}>
              <SiteListRenewalsTab contracts={renewalContracts} />
            </Tab>
          )}

          <Tab key="bill-imports" title="Bill Imports">
            <SiteListBillImportsTab
              billImports={billImports}
              isLoading={billImportsLoading}
              onDeleteBillImport={handleDeleteBillImport}
              onUploadBills={onBillUploadOpen}
              onRetryBillImport={handleRetryBillImport}
              retryingBillImportId={
                retryBillImportMutation.isPending ? (retryBillImportMutation.variables ?? null) : null
              }
              onReExtractAll={handleReExtractAll}
              isReExtracting={reExtractBillsMutation.isPending}
              isDeleting={deleteBillImportMutation.isPending}
            />
          </Tab>

          <Tab
            key="exports"
            title={
              <span className="flex items-center gap-1.5">
                Exports
                {exportRunning && <Spinner size="sm" className="scale-[0.6] -mx-1" />}
                {unseenExportCount > 0 && (
                  <span className="min-w-[18px] h-[18px] px-1.5 rounded-full bg-primary text-white text-[11px] font-semibold inline-flex items-center justify-center">
                    {unseenExportCount}
                  </span>
                )}
              </span>
            }
          >
            <SiteListExportsTab
              exportJobs={exportJobs}
              seenJobIds={seenExportIds ?? new Set()}
              onDownloadExport={handleDownloadExport}
              onDismissExport={handleDismissExport}
            />
          </Tab>

          {hasSmeSites && (
            <Tab
              key="checklist"
              title={`Checklist (${countTenderChecklistTicks(siteList)}/${TENDER_CHECKLIST_TASK_COUNT})`}
            >
              <TenderChecklistTab siteList={siteList} userName={currentUser?.displayName ?? 'Unknown'} />
            </Tab>
          )}
        </Tabs>
      </div>

      <CloseReviewDrawer
        site={reviewSite}
        siteListId={siteListId}
        userName={currentUser?.displayName ?? 'Unknown'}
        onClose={() => setReviewSite(null)}
      />

      <AddSiteModal
        isOpen={isAddSiteOpen}
        onClose={onAddSiteClose}
        siteListId={siteListId}
        onSiteAdded={() => {
          // The mutation will handle invalidating queries
        }}
      />

      <LinkExistingSiteModal
        isOpen={isLinkExistingSiteOpen}
        onClose={onLinkExistingSiteClose}
        siteListId={siteListId}
        onSiteLinked={() => {
          // The mutation will handle invalidating queries
        }}
      />

      <BillUploadModal isOpen={isBillUploadOpen} onClose={onBillUploadClose} siteListId={siteListId} />

      <ImportSitesCsvModal isOpen={isImportSitesCsvOpen} onClose={onImportSitesCsvClose} siteListId={siteListId} />

      {reviewSiteImport && (
        <SiteImportReviewModal
          isOpen
          onClose={() => setReviewSiteImportId(null)}
          siteListId={siteListId}
          siteImport={reviewSiteImport}
        />
      )}

      {siteList && (
        <DeleteSiteListModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          siteList={siteList}
          onDeleted={handleDeleteListCompleted}
        />
      )}

      <LinkDealModal isOpen={isLinkDealOpen} onClose={onLinkDealClose} siteListId={siteListId} />
      <LinkContractToSiteListModal isOpen={isLinkContractOpen} onClose={onLinkContractClose} siteListId={siteListId} />
      <PrepareLoaModal
        isOpen={isPrepareLoaOpen}
        onClose={onPrepareLoaClose}
        siteListId={siteListId}
        defaultSignerName={loaPrimaryContact?.name}
        defaultSignerEmail={loaPrimaryContact?.email}
        defaultSubmissionName={siteList ? `LOA — ${siteList.name}` : undefined}
      />

      <RequestBillsModal isOpen={isRequestBillsOpen} onClose={onRequestBillsClose} siteListId={siteListId} />

      <MoveSiteToListModal
        isOpen={isMoveModalOpen}
        onClose={onMoveModalClose}
        siteIds={sitesToMove}
        fromSiteListId={siteListId}
        fromSiteListName={siteList?.name}
      />
    </div>
  );
}
